import { Component, OnInit } from '@angular/core';
import { BaseWidgetSettingsComponent } from '../../widget/base/base-widget';
import { WidgetDataService } from '../../widget/data/widget-data.service';
import { IChartWidgetConfig } from '../chart.component';
import { DxButtonModule, DxFormModule, DxPopupModule } from 'devextreme-angular';
import { MachineInfoMainService } from 'src/app/shared/services/machine-info.service';

import { FieldDataChangedEvent } from 'devextreme/ui/form';
import { RemoteDataWrapperService } from 'src/app/shared/services/remote-data/remote-data-wrapper.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AvailableSeriesData } from 'src/app/shared/services/remote-data/model';
import { MachineInfo } from '../../../../../../api/generated';


/**
 * Settings for chart components - ChartWidgetComponent and ChartCanvaWidgetComponent
 *
 * @export
 * @class ChartSettingsComponent
 * @extends {BaseWidgetSettingsComponent<IChartWidgetConfig>}
 * @implements {OnInit}
 */
@Component({
  selector: 'app-chart-settings',
  standalone: true,
  imports: [DxFormModule, DxPopupModule, DxButtonModule, CommonModule],
  templateUrl: './chart-settings.component.html',
  styleUrl: './chart-settings.component.scss'
})
export class ChartSettingsComponent extends BaseWidgetSettingsComponent<IChartWidgetConfig> implements OnInit {

  /**
   * Machine name select data source.
   *
   * @type {{ name: string }[]}
   * @memberof ChartSettingsComponent
   */
  public selectOptions: { name: string }[] = [];
  
  /**
   * Subscription of series.
   *
   * @private
   * @type {Subscription}
   * @memberof ChartSettingsComponent
   */
  private _seriesSubscription?: Subscription;
  
  /**
   * Data source for chart series type.
   *
   * @type {{key: string; value: string;}[]}
   * @memberof ChartSettingsComponent
   */
  public graphTypeSource: {key: string; value: string;}[] = [
    {key: 'barSeries', value: 'Bar'},
    {key: 'lineSeries', value: 'Line'}
  ];

  
  /**
   * Callback which returns current value of auto-scale form input.
   *
   * @memberof ChartSettingsComponent
   */
  public isAutoScaleCallback = (): boolean => {
    return this.formData?.valueAxisAutoSacle === true;
  }

  
  /**
   * Callback which returns current value of auto-scale form input.
   *
   * @memberof ChartSettingsComponent
   */
  public isFixedSacleCallback = (): boolean => {
    return this.formData?.valueAxisAutoSacle === false;
  }

  
  /**
   * Callback which returns current value of chart series type form input.
   *
   * @memberof ChartSettingsComponent
   */
  public isLineTypeCallback = (): boolean => {
    return this.formData?.graphType === 'lineSeries';
  }
  
  
  /**
   * Datasource of series select.
   *
   * @type {string[]}
   * @memberof ChartSettingsComponent
   */
  public seriesSource: string[] = [];

  
  /**
   * Last series response from client.
   *
   * @type {IAvailableSeriesResponse}
   * @memberof ChartSettingsComponent
   */
  public currentSeriesResponse: AvailableSeriesData = {_t: "AvailableSeriesData", BarSeries: [], LineSeries: []};

  constructor(
    widgetDataService: WidgetDataService<IChartWidgetConfig>, 
    private readonly _miSvc: MachineInfoMainService,
    private readonly _remoteDataWrapper: RemoteDataWrapperService,
  ) {
    super(widgetDataService);

    this.formData = {
      series: '',
      argumentField: 'arg',
      valueField: 'val',
      bufferSize: 100,
      valueVisualRange: { min: 0, max: 100 },
      machineName: '',
      title: 'Chart',
      graphType: '',
      valueAxisAutoSacle: false,
      tickInterval: undefined
    };
  }

  
  /**
   * Initialization of data source.
   *
   * @memberof ChartSettingsComponent
   */
  ngOnInit(): void {
    this._miSvc.dataSource.load().then((data: MachineInfo[]) => {
      this.selectOptions = data.map((r: MachineInfo) => {
        return { name: r.Name ?? '' };
      });

      if (this._miSvc.CurrentMachine?.Name && this.formData && this.formData.machineName === '') {
        this.formData.machineName = this._miSvc.CurrentMachine.Name;
      }
    });
  }

  
  /**
   * Setting form data of current widget from WidgetComponent
   *
   * @memberof ChartSettingsComponent
   */
  public override setFormData(data: IChartWidgetConfig): void {
      this.formData = {...data};

      this._seriesSubscription?.unsubscribe();

      this._seriesSubscription = this._remoteDataWrapper.getAvailableSeries(this.formData.machineName ?? "").subscribe(val => {
        this.currentSeriesResponse = val;

        this._handleGraphTypeChange(this.formData?.graphType ?? '');
      });
  }

  
  /**
   * Make reaction of some important fileds in form.
   *
   * @param {FieldDataChangedEvent} event
   * @memberof ChartSettingsComponent
   */
  public onFormChanged(event: FieldDataChangedEvent): void {
    if (event.dataField === 'machineName') {
      this._seriesSubscription?.unsubscribe();

      this.seriesSource = [];

      this._seriesSubscription = this._remoteDataWrapper.getAvailableSeries(event.value).subscribe(val => {
        this.currentSeriesResponse = val;

        if (this.formData?.graphType) {
          this._handleGraphTypeChange(this.formData.graphType);
        }

        if (this.formData && !this.seriesSource.includes(this.formData.series)) {
          this.formData.series = '';
        }
      });
    }

    if (event.dataField === 'graphType') {
      this.formData ? this.formData.series = '' : '';
      this._handleGraphTypeChange(event.value);
    }
  }

  
  /**
   * Form submit method with validation.
   *
   * @memberof ChartSettingsComponent
   */
  public override onSubmitModal(): void {
    if (this.formData) {
      this.formData.title = this.formData.series;
      this.formData.subtitle = this.formData.machineName;
    }
    super.onSubmitModal();
  }

  
  /**
   * Handles series data source on chart type changes.
   *
   * @private
   * @param {string} graphType
   * @memberof ChartSettingsComponent
   */
  private _handleGraphTypeChange(graphType: string): void {
    switch (graphType) {
      case 'barSeries': this.seriesSource = this.currentSeriesResponse.BarSeries; break;
      case 'lineSeries': this.seriesSource = this.currentSeriesResponse.LineSeries; break;
      default: throw Error('Unsuported chart type.');
    }
  }
}
